import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { finalize, mergeMap, Subject, Subscription, takeUntil } from 'rxjs';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { MapModelLike } from 'app/shared/models/map.model';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import { MapsService } from 'app/shared/services/maps.service';
import { ToastService } from 'app/shared/services/toast.service';
import saveFileAs from 'file-saver';
import { FileUtils } from 'app/shared/utils/file.utils';
import { RxFileReader } from 'rxjs-filereader';
import { AppConfigService } from 'app/shared/services/app-config.service';
import { ScreenService } from 'app/shared/services/screen.service';
import { TranslationService } from '@services/translation.service';
import { availableLanguages } from 'translations';

interface ColumnOption {
  name: string;
  label?: string;
}

@Component({
  selector: 'app-maps-management',
  templateUrl: 'maps-management.component.html',
  styleUrls: ['maps-management.component.scss'],
})
export class MapsManagementComponent implements OnInit, OnDestroy {
  @ViewChild('inputFileEL', { static: false })
  inputFileElRef: ElementRef<HTMLInputElement>;

  @Output()
  onMapLoaded = new EventEmitter<any>();

  modelToEdit: MapModelLike;
  mapRegisterModalVisible = false;
  mapDeleteModalVisible = false;

  resultList: MapModelLike[];

  _pagedData: any[];
  pageIndex = 0;
  pageSize = 4;

  columnsToDisplay: ColumnOption[];
  displayedColumns: string[];

  get isBtnImportMapVisible() {
    return !this.appConfig.siiaMode;
  }

  loadingController = new LoadingController();

  private listMapsSubscription: Subscription;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private appConfig: AppConfigService,
    private mapsService: MapsService,
    private mainMapService: MainMapService,
    private toastService: ToastService,
    private globalLoadingController: LoadingController,
    private screenService: ScreenService,
    private ts: TranslationService
  ) {}

  ngOnInit(): void {
    this.columnsToDisplay = [
      {
        name: 'name',
        label: 'Nome do Mapa',
      },
      {
        name: 'description',
        label: 'Descrição',
      },
    ];

    this.displayedColumns = [
      'actions',
      ...this.columnsToDisplay.map((x) => x.name),
    ];

    this.loadMapsList();
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onPageIndexChange(pageIndex: number) {
    this.pageIndex = pageIndex;

    const lastPageIndex =
      Math.ceil((this.resultList?.length ?? 0) / this.pageSize) - 1 || 0;
    const newPageIndex = Math.min(pageIndex, lastPageIndex);

    const start = newPageIndex * this.pageSize;

    this._pagedData =
      this.resultList?.slice(start, start + this.pageSize) ?? [];
  }

  onBtnLoadMapClick(model: MapModelLike) {
    if (this.globalLoadingController.isShown()) {
      return;
    }

    this.globalLoadingController.show();
    this.mapsService
      .getMapById(model.id)
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (item) => {
          this.mainMapService.loadMap(item);

          this.onMapLoaded.emit(item);

          this.toastService.success(
            this.ts.getCurrentLanguage()['map-loaded-successfully']
          );
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-load-map']
          );
        },
      });
  }

  onBtnEditClick(model: MapModelLike) {
    this.modelToEdit = model;
    this.mapRegisterModalVisible = true;
  }

  onBtnDeleteClick(model: MapModelLike) {
    this.modelToEdit = model;
    this.mapDeleteModalVisible = true;
  }

  onBtnShareClick(model: MapModelLike) {
    if (this.appConfig.siiaMode) {
      this.globalLoadingController.show();
      this.mapsService
        .shareMapWithMyEntity(model.id)
        .pipe(
          finalize(() => {
            this.globalLoadingController.hide();
          }),
          takeUntil(this.lifecycle)
        )
        .subscribe({
          next: () => {
            this.toastService.success(
              this.ts.getCurrentLanguage()['map-shared-successfully']
            );
          },
          error: (response) => {
            if (!response) {
              return;
            }

            this.toastService.error(
              this.ts.getCurrentLanguage()['failed-share-map']
            );
          },
        });
    } else {
      const filename = `${model.name}.txt`;

      const file = FileUtils.writeFileFromText(model.id, filename);

      saveFileAs(file, filename);
    }
  }

  onBtnImportMapClick() {
    this.inputFileElRef?.nativeElement?.click();
  }

  onFileChange() {
    if (this.globalLoadingController.isShown()) {
      return;
    }

    const file = this.inputFileElRef?.nativeElement?.files?.item(0);

    if (!file) {
      return;
    }

    this.inputFileElRef.nativeElement.value = '';

    this.globalLoadingController.show();

    RxFileReader.readAsText(file)
      .pipe(
        mergeMap((mapId) => {
          return this.mapsService.copyMap(mapId);
        })
      )
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: () => {
          this.toastService.success(
            this.ts.getCurrentLanguage()['map-imported-successfully']
          );

          this.loadMapsList();
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-import-map']
          );
        },
      });
  }

  onBtnFavoriteClick(model: MapModelLike) {
    if (this.globalLoadingController.isShown()) {
      return;
    }

    this.globalLoadingController.show();
    this.mapsService
      .setAsFavorite(model.id, !model.favorite)
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: () => {
          if (this.ts.getCurrentLanguageCode() === availableLanguages.en) {
            this.toastService.success(
              `${this.ts.getCurrentLanguage().map} ${
                model.favorite ? 'un' : ''
              }${this.ts.getCurrentLanguage()['marked-favorite-successfully']}`
            );
          }

          if (this.ts.getCurrentLanguageCode() === availableLanguages.en) {
            this.toastService.success(
              `${this.ts.getCurrentLanguage().map} ${
                model.favorite ? 'des' : ''
              }${this.ts.getCurrentLanguage()['marked-favorite-successfully']}`
            );
          }

          this.loadMapsList();
        },
        error: (response) => {
          if (!response) {
            return;
          }

          if (this.ts.getCurrentLanguageCode() === availableLanguages.en) {
            this.toastService.success(
              `${this.ts.getCurrentLanguage()['failed-to']} ${
                model.favorite ? 'un' : ''
              }${this.ts.getCurrentLanguage()['mark-map-as-favorite']}`
            );
          }

          if (this.ts.getCurrentLanguageCode() === availableLanguages.en) {
            this.toastService.success(
              `${this.ts.getCurrentLanguage()['failed-to']} ${
                model.favorite ? 'des' : ''
              }${this.ts.getCurrentLanguage()['mark-map-as-favorite']}`
            );
          }
        },
      });
  }

  onBtnDoDeleteClick() {
    if (this.modelToEdit && !this.globalLoadingController.isShown()) {
      this.globalLoadingController.show();
      this.mapsService
        .deleteMap(this.modelToEdit.id)
        .pipe(
          finalize(() => {
            this.globalLoadingController.hide();
          }),
          takeUntil(this.lifecycle)
        )
        .subscribe({
          next: () => {
            this.toastService.success(
              this.ts.getCurrentLanguage()['map-deleted-successfully']
            );

            this.mapDeleteModalVisible = false;

            this.loadMapsList();
          },
          error: (response) => {
            if (!response) {
              return;
            }

            this.toastService.error(
              this.ts.getCurrentLanguage()['failed-delete-record']
            );
          },
        });
    }
  }

  onSaved() {
    this.loadMapsList();
  }

  onRegisterModalClosed() {
    this.modelToEdit = null;
  }

  isMapLoaded(model: MapModelLike) {
    return this.mainMapService.isMapLoaded(model);
  }

  private loadMapsList() {
    this.listMapsSubscription?.unsubscribe();

    this.loadingController.show();
    this.listMapsSubscription = this.mapsService
      .listMaps()
      .pipe(
        finalize(() => {
          this.loadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (list) => {
          this.resultList = list;

          this.onPageIndexChange(0);
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-load-data']
          );
        },
      });
  }
}
